/**
 * useFlashBooking.ts
 * Scroll a booking bar in the machine grid into view and PULSE it hard so it's
 * impossible to miss: a thick coloured ring + glow plus a gentle scale, pulsed
 * three times (~1.8s). Two callers:
 *   • Conflicts panel — flashes the conflicting bar in red (jump-to-conflict).
 *   • Clicking a row in the top "Project Tasks" table — flashes the matching bar
 *     in primary sage so the planner can spot which one it is.
 *
 * Bars carry a `data-booking-key` attribute, queried from the grid container.
 * Uses the Web Animations API so there are no leftover inline styles to clean up
 * and the pulse can't get stuck if a second flash interrupts it.
 * Extracted from Phase1Panel to keep that file within the 600-line cap.
 */
import { useCallback } from 'react';

export function useFlashBooking(
  containerRef: React.RefObject<HTMLElement | null>,
): (bookingKey: string, color: string) => void {
  return useCallback(
    (bookingKey: string, color: string) => {
      const el = containerRef.current?.querySelector<HTMLElement>(
        `[data-booking-key="${bookingKey}"]`,
      );
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

      // Raise the bar above its neighbours so the ring/glow isn't hidden behind
      // adjacent bars, then restore afterwards.
      const prevZ = el.style.zIndex;
      const prevPos = el.style.position;
      if (!prevPos) el.style.position = 'relative';
      el.style.zIndex = '6';

      const ring = `0 0 0 3px ${color}, 0 0 16px 5px ${color}`;
      const anim = el.animate(
        [
          { boxShadow: '0 0 0 0 transparent', transform: 'scale(1)' },
          { boxShadow: ring, transform: 'scale(1.06)' },
          { boxShadow: '0 0 0 0 transparent', transform: 'scale(1)' },
        ],
        { duration: 600, iterations: 3, easing: 'ease-in-out' },
      );
      const restore = () => {
        el.style.zIndex = prevZ;
        el.style.position = prevPos;
      };
      anim.onfinish = restore;
      anim.oncancel = restore;
    },
    [containerRef],
  );
}
